/**
 * The panes behind a rail view that is not a conversation list: one server
 * folder (a mailbox's Sent, or a folder under "More folders"), or the combined
 * Sent of every mailbox.
 *
 * Kept beside the rail rather than in `EmailSection`: it is the rail's views
 * turned into folder reads, and the section only has to draw what comes back.
 */
import { useCallback, useEffect } from "react";
import type { MailThread } from "../../../../lib/mail";
import { useImapBrowse, useImapMessageActions, useSentAll, type ImapSource, type SentAllRow } from "../imap";
import { shortAddress } from "./labels";
import type { MailRail } from "./useMailRail";
import { START_VIEW, type MailView } from "./view";

export function useRailPanes(mailView: MailView, setMailView: (view: MailView) => void, rail: MailRail) {
  // A mailbox's Sent row is its server's Sent folder, found in its folder
  // list; until that list is in, the pane says it is loading.
  const sentFolder = mailView.kind === "sent" ? rail.sentOf(mailView.mailbox) : null;
  const imapSource: ImapSource | null =
    mailView.kind === "folder"
      ? { mailbox: mailView.mailbox, path: mailView.path }
      : mailView.kind === "sent" && sentFolder
        ? { mailbox: mailView.mailbox, path: sentFolder }
        : null;
  /** The open folder's path; "" for a Sent still being looked up; null when no folder is open. */
  const imapPath = imapSource?.path ?? (mailView.kind === "sent" ? "" : null);
  const imap = useImapBrowse(imapSource);
  const imapActions = useImapMessageActions(imap, imapSource);

  const sentAll = useSentAll(
    mailView.kind === "sentAll"
      ? rail.mailboxes.flatMap((m) => {
          const path = m.imap && m.imapOpen ? rail.sentOf(m.id) : null;
          return path ? [{ mailbox: m.id, path }] : [];
        })
      : null,
  );

  // A Sent opened from Favorites may belong to a folded mailbox, whose tree
  // never read its folders.
  const ensureFolders = rail.ensureFolders;
  useEffect(() => {
    if (mailView.kind === "sent") ensureFolders(mailView.mailbox);
    if (mailView.kind === "sentAll") {
      for (const m of rail.mailboxes) if (m.imap && m.imapOpen) ensureFolders(m.id);
    }
  }, [mailView, rail.mailboxes, ensureFolders]);

  // A mailbox's server folders are only offered while its mail is being read.
  // When that goes off (or the mailbox is removed) the rail stops drawing them,
  // and an open folder would otherwise hold both panes with nothing in the rail
  // marked and no way back to the inbox.
  useEffect(() => {
    if (mailView.kind !== "folder" && mailView.kind !== "sent") return;
    if (!rail.mailboxes.length) return;
    const box = rail.mailboxes.find((m) => m.id === mailView.mailbox);
    if (!box || !box.imap) setMailView(START_VIEW);
  }, [mailView, rail.mailboxes, setMailView]);

  /** The address a letter written in this mailbox starts from, when it is one of the reader's own. */
  const ownAddress = useCallback(
    (mailbox: string | null | undefined) =>
      mailbox ? rail.mailboxes.find((m) => m.id === mailbox && m.kind === "own")?.address : undefined,
    [rail.mailboxes],
  );

  /** In a combined list each row says whose mailbox it is in; with one mailbox, nothing needs saying. */
  const many = rail.mailboxes.length > 1;
  const nameOf = (id: string) => shortAddress(rail.mailboxes.find((m) => m.id === id)?.address ?? "");
  const threadTag = many ? (row: MailThread) => nameOf(row.mailboxId ?? "shared") : undefined;
  const sentTag = (row: SentAllRow) => (many ? nameOf(row.mailbox) : null);

  return { sentFolder, imapSource, imapPath, imap, imapActions, sentAll, ownAddress, threadTag, sentTag };
}
